import { useMemo, useState } from 'react';
import { usePhoneCrumb } from '../lib/usePhoneCrumb';
import { useNavigate } from 'react-router-dom';
import { Card, DataTable, EmptyState, Pane, PaneHead, type TableRow } from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useInboundReferrals, usePatientSources } from '../lib/useManagerData';
import { useNarrowViewport } from '@/components/kit/useNarrow';
import { MonthFilterRow } from '../components/MonthFilterRow';
import { Bar } from '../components/Charts';
import {
  lastCompleteMonth, monthLabel, monthName, monthRange, num, shiftMonth,
  monthChip,
} from '../lib/scope';
import { exportCsv } from '@/pages/roles/lib/exportCsv';
import { ManagerSurface } from '../components/ManagerSurface';
import { ManagerMenu } from '../components/ManagerMenu';

/**
 * Clinic manager · Patient sources — `g_sources.dc.html` / phone `g_r4`.
 *
 * "Fed by the one dropdown on the registration form — the desk asks, once."
 * Both Add-patient forms ask it (`heardAboutUs`, "<channel> — <who>"), and
 * `GET /patients/sources` counts the month's new patients by the channel
 * part. A patient nobody asked is "Not said", counted, so the rows always add
 * up to the month's new patients. Referrals logged against a visit are a
 * second, independent record — the pane for "Referred by Dr." shows both.
 */
const CHANNEL_SUB: Record<string, string> = {
  'Referred by Dr.': 'the specialist’s own marketing channel',
  'Referred by a patient': 'the patients who were told about you',
  Google: 'searched and found you',
  'Facebook or Instagram': 'the page and its posts',
  'Walked past': 'the sign on the street',
  'Insurer’s list': 'found you on their insurer’s network',
  Other: 'in the desk’s own words',
  'Not said': 'registered without the question answered',
};

export function ManagerSourcesPage() {
  const go = useNavigate();
  const [month, setMonth] = useState(lastCompleteMonth());
  const [compare, setCompare] = useState(shiftMonth(lastCompleteMonth(), -1));
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const range = useMemo(() => monthRange(month), [month]);
  const prevRange = useMemo(() => monthRange(compare), [compare]);

  const now = usePatientSources(range);
  const before = usePatientSources(prevRange);
  const referrals = useInboundReferrals(range);
  const narrow = useNarrowViewport();

  usePageMeta({ title: 'Patient sources' });

  interface Source {
    id: string;
    name: string;
    sub: string;
    patients: number;
    before: number;
    details: Array<{ detail: string; count: number }>;
  }

  const sources: Source[] = now.channels.map((c) => ({
    id: c.channel,
    name: c.channel,
    sub: CHANNEL_SUB[c.channel] ?? 'as the desk wrote it',
    patients: c.count,
    before: before.channels.find((b) => b.channel === c.channel)?.count ?? 0,
    details: c.details,
  }));
  const sig = sources.map((x) => `${x.id}:${x.patients}:${x.before}`).join('|');

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sources;
    return sources.filter((x) =>
      `${x.name} ${x.sub} ${x.details.map((d) => d.detail).join(' ')}`.toLowerCase().includes(q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, sig]);

  const selected = selectedId ? sources.find((x) => x.id === selectedId) ?? null : null;
  const total = now.total;
  const said = sources.filter((x) => x.id !== 'Not said');

  const rows: TableRow[] = shown.map((s) => ({
    id: s.id,
    cells: {
      source: (
        <div>
          <p className="t-strong">{s.name}</p>
          <p className="t-cap mut">{s.sub}</p>
        </div>
      ),
      patients: <span className="w6 num">{num(s.patients)}</span>,
      trend: (() => {
        const t = s.before ? Math.round(((s.patients - s.before) / s.before) * 100) : null;
        return (
          <span className={t === null ? 'mut' : t > 0 ? 'grn w6' : t < 0 ? 'amb w6' : 'mut'}
            title={t === null
              ? `${monthName(compare)} has nothing to compare this month against`
              : `${s.patients} this month against ${s.before} in ${monthName(compare)}`}>
            {t === null ? '—' : t > 0 ? '▲' : t < 0 ? '▼' : '='}
          </span>
        );
      })(),
    },
  }));

  const crumb = usePhoneCrumb('Reports', `Sources · ${monthChip(month).split(' ')[0]}`);
  useShellChrome(
    {
      raw: true,
      ...crumb,
      /* Phone: the pick is its own screen; back clears it. */
      paneOpen: !!selected,
      onPaneClose: () => setSelectedId(null),
      phoneMenu: (close) => <ManagerMenu door="reports" onClose={close} />,
      title: 'Patient sources',
      sub: `${num(total)} new patient${total === 1 ? '' : 's'} · ${monthLabel(month)}`,
      search: 'Search a source, a referrer…',
      searchValue: query,
      onSearchChange: setQuery,
      primary: {
        label: 'Export',
        onClick: () =>
          exportCsv(`patient-sources-${month}`, ['Source', 'Patients'],
            sources.map((s) => [s.name, s.patients])),
      },
      pane: selected ? (
        <Pane
          head={
            <PaneHead
              overline="The selected source"
              title={`${selected.name} — ${num(selected.patients)}`}
              sub={selected.sub}
            />
          }
          foot={
            selected.id === 'Referred by Dr.' ? (
              <button type="button" className="btn btn-outline" style={{ flex: 1 }}
                onClick={() => go('/directory')}>
                Open the Directory ›
              </button>
            ) : undefined
          }
        >
          <Card>
            <p className="t-ovl mut">{selected.id === 'Referred by Dr.' ? 'Which doctor' : 'What the desk wrote'}</p>
            {selected.details.length ? (
              selected.details.map((d) => (
                <div key={d.detail} className="flex" style={{ justifyContent: 'space-between', marginTop: 4 }}>
                  <p className="t-sec sec">{d.detail}</p>
                  <p className="t-sec w6 num">{d.count}</p>
                </div>
              ))
            ) : (
              <p className="t-sec mut" style={{ marginTop: 4 }}>
                {selected.id === 'Not said' ? 'Nobody asked these patients.' : 'Nothing written beyond the channel.'}
              </p>
            )}
          </Card>
          {selected.id === 'Referred by Dr.' ? (
            <Card>
              <p className="t-ovl mut">Referral letters logged</p>
              {referrals.byProvider.length ? (
                referrals.byProvider.slice(0, 6).map((r) => (
                  <div key={r.name} className="flex" style={{ justifyContent: 'space-between', marginTop: 4 }}>
                    <p className="t-sec sec">{r.name}</p>
                    <p className="t-sec w6 num">{r.count}</p>
                  </div>
                ))
              ) : (
                <p className="t-sec mut" style={{ marginTop: 4 }}>No inbound referral was logged this month.</p>
              )}
            </Card>
          ) : null}
        </Pane>
      ) : undefined,
    },
    [month, compare, query, selectedId, sig, total, referrals.count],
  );

  return (
    <div className="maie desk">
      <ManagerSurface />
      <div className="dcontent">
        <MonthFilterRow
          month={month} onMonth={(m) => { setMonth(m); setSelectedId(null); }}
          compare={compare} onCompare={setCompare}
          branchLine="all branches"
        />

        <div className="cm-split" style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
          <div style={{ flex: 1.2, minWidth: 0 }}>
            <DataTable
              caption="Patient sources"
              columns={[
                { key: 'source', label: 'Source', width: 52 },
                { key: 'patients', label: 'Patients', width: 24 },
                { key: 'trend', label: `vs ${monthName(compare)}`, width: 24 },
              ]}
              rows={rows}
              selectedId={selectedId}
              onSelect={(r) => setSelectedId((cur) => (cur === r.id ? null : r.id))}
              empty={now.isLoading
                ? <EmptyState title="Counting…" />
                : query
                  ? <EmptyState title="No source matches that" sub="Clear the search to see every channel again." />
                  : <EmptyState title="Nobody new this month" sub="A patient registered in the month lands on the channel the desk picked." />}
            />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Card>
              <p className="t-ovl mut mb8">The mix</p>
              {said.length ? (
                said.map((x) => (
                  <Bar key={x.id} label={x.name} value={x.patients} max={total || 1}
                    display={`${Math.round((x.patients / (total || 1)) * 100)}%`} />
                ))
              ) : (
                <p className="t-sec mut">
                  Nothing to split: {total ? 'no new patient this month was asked' : 'no patients were registered this month'}.
                </p>
              )}
            </Card>
          </div>
        </div>

        {/* `g_r4` — a phone has no pane to open, so the referrers the pane lists
            come up under the sources as their own short list. */}
        {narrow && referrals.byProvider.length ? (
          <>
            <p className="t-ovl mut" style={{ margin: '16px 0 8px' }}>Top referrers</p>
            {referrals.byProvider.slice(0, 3).map((r) => (
              <Card key={r.name}>
                <div className="flex" style={{ justifyContent: 'space-between' }}>
                  <p className="t-strong">{r.name}</p>
                  <p className="t-sec w6 num">{r.count} patients</p>
                </div>
              </Card>
            ))}
          </>
        ) : null}

        <p className="t-sec mut" style={{ marginTop: 12 }}>
          {num(total)} new patient{total === 1 ? '' : 's'} in{' '}
          {monthLabel(month).split(' ')[0]}, and which marketing worked — in patients, not
          impressions.
        </p>
      </div>
    </div>
  );
}
